import React from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import VideocamOffIcon from '@mui/icons-material/VideocamOff'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import { t } from '@lingui/macro'

interface NoRemoteVideoProps {
  participantName?: string
  participantRole?: string
  statusLabel?: string
  isRemotePresent?: boolean
  /** Smaller variant for the local self-view PIP. Identical to the full size on mobile. */
  compact?: boolean
}

const colors = {
  surface: '#f1f5f9',
  onSurface: '#191c1e',
  onSurfaceMuted: '#64748b',
  avatarText: '#94a3b8',
  present: '#10b981',
  waiting: '#f59e0b',
  cameraOffText: '#78350f',
  waitingText: '#064e3b',
} as const

function getInitials(name?: string) {
  if (!name) return ''
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}

/** Responsive sizing — identical to the full size on mobile (xs/sm); only the desktop PIP (md) shrinks. */
function getSizes(compact?: boolean) {
  return {
    avatarSize: compact ? { xs: 128, sm: 160, md: 96 } : { xs: 128, sm: 160, md: 224 },
    avatarFontSize: compact ? { xs: 40, sm: 48, md: 30 } : { xs: 40, sm: 48, md: 72 },
    stackSpacing: compact ? { xs: 4, md: 1.5 } : 4,
    nameFontSize: compact ? { xs: '1.25rem', md: '0.95rem' } : undefined,
  }
}

type PlaceholderSizes = ReturnType<typeof getSizes>

function PlaceholderAvatar({ initials, size, fontSize, present }: { initials: string; size: PlaceholderSizes['avatarSize']; fontSize: PlaceholderSizes['avatarFontSize']; present?: boolean }) {
  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #f8fafc 0%, rgba(98,27,203,0.05) 100%)',
          color: colors.avatarText,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize,
          fontWeight: 300,
          letterSpacing: '-0.025em',
          border: '4px solid rgba(98, 27, 203, 0.1)',
          boxShadow: '0 0 80px rgba(98, 27, 203, 0.08)',
        }}
      >
        {initials || '•••'}
      </Box>
      <Box
        sx={{
          position: 'absolute',
          bottom: 16,
          right: 16,
          width: 24,
          height: 24,
          borderRadius: '50%',
          bgcolor: present ? colors.present : colors.waiting,
          border: '4px solid #ffffff',
        }}
      />
    </Box>
  )
}

function ParticipantIdentity({ name, role, nameFontSize }: { name: string; role?: string; nameFontSize?: PlaceholderSizes['nameFontSize'] }) {
  return (
    <Box>
      <Typography variant="h6" sx={{ color: colors.onSurface, fontWeight: 600, lineHeight: 1.2, fontSize: nameFontSize }}>
        {name}
      </Typography>
      {role && (
        <Typography variant="body2" sx={{ color: colors.onSurfaceMuted, mt: 0.5 }}>
          {role}
        </Typography>
      )}
    </Box>
  )
}

function StatusChip({ label, cameraOff }: { label: string; cameraOff: boolean }) {
  return (
    <Chip
      icon={
        cameraOff ? (
          <VideocamOffIcon sx={{ fontSize: 18, color: '#d97706 !important' }} />
        ) : (
          <FiberManualRecordIcon sx={{ fontSize: 12, color: '#059669 !important' }} />
        )
      }
      label={label}
      sx={{
        bgcolor: cameraOff ? 'rgba(245, 158, 11, 0.1)' : 'rgba(16, 185, 129, 0.1)',
        border: cameraOff ? '1px solid rgba(245, 158, 11, 0.2)' : '1px solid rgba(16, 185, 129, 0.2)',
        color: cameraOff ? colors.cameraOffText : colors.waitingText,
        backdropFilter: 'blur(12px)',
        fontWeight: 600,
        px: 2,
        py: 2.5,
        '& .MuiChip-icon': { ml: 1 },
      }}
    />
  )
}

export function NoRemoteVideo({ participantName, participantRole, statusLabel, isRemotePresent, compact }: NoRemoteVideoProps) {
  const label = statusLabel ?? (isRemotePresent ? t`Patient camera off` : t`Waiting for the patient...`)
  const { avatarSize, avatarFontSize, stackSpacing, nameFontSize } = getSizes(compact)

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        bgcolor: colors.surface,
        color: colors.onSurface,
        backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(234, 221, 255, 0.55) 0%, transparent 70%)',
      }}
    >
      <Stack spacing={stackSpacing} alignItems="center" textAlign="center" px={3} sx={{ position: 'relative', zIndex: 1 }}>
        <PlaceholderAvatar initials={getInitials(participantName)} size={avatarSize} fontSize={avatarFontSize} present={isRemotePresent} />
        {participantName && <ParticipantIdentity name={participantName} role={participantRole} nameFontSize={nameFontSize} />}
        <StatusChip label={label} cameraOff={!!isRemotePresent} />
      </Stack>
    </Box>
  )
}

export default NoRemoteVideo
